import React, { useState } from 'react';
import { DropdownMenu, SparkIcon, TooltipTrigger, TruncatedText } from '@spark';
import { HelpIcon } from '../shared/TaskTypeControls';
import {
    BEST_GUESS_LABEL,
    BEST_GUESS_TOOLTIP,
    BEST_GUESS_VALUE,
    NO_PREDICTIONS_LABEL,
    NO_PREDICTIONS_TOOLTIP,
    NO_PREDICTIONS_VALUE,
} from './fallbackLabel';

export {
    BEST_GUESS_VALUE,
    NO_PREDICTIONS_VALUE,
} from './fallbackLabel';

const OptionHelpIcon = ({ text }) => (
    <TooltipTrigger text={text}>
        <span
            className="cw-help-icon"
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <SparkIcon slug="circle-help" size="small" />
        </span>
    </TooltipTrigger>
);

/**
 * "Fallback label" single-select for classification tasks. Shown in Step 1
 * (Business Goals) and reused in the Edit Task modal. Options are
 * "No predictions" plus the labels passed in; single-label also includes
 * "Best guess". They follow label edits live. The field is required whenever
 * it is shown. "Best guess" maps to confidence rejection off (no fallback
 * label); "No predictions" keeps rejection on with an empty prediction.
 *
 * The menu matches LabelPrioritySelect: the panel grows to the longest
 * option (`cw-label-options-panel`) instead of ellipsizing at the trigger
 * width, and TruncatedText tooltips only an option the viewport cap still
 * clips. `searchText` carries the plain label for the trigger search and for
 * DropdownMenu's item keys / keyboard highlight, since the label is a node.
 */
const FallbackLabelSelect = ({
    labels = [],
    value,
    onChange,
    error,
    required = true,
    classificationType,
}) => {
    const [open, setOpen] = useState(false);
    const isMultilabel = classificationType === 'multi';

    const options = [
        ...(!isMultilabel
            ? [{ value: BEST_GUESS_VALUE, label: BEST_GUESS_LABEL, helpText: BEST_GUESS_TOOLTIP }]
            : []),
        { value: NO_PREDICTIONS_VALUE, label: NO_PREDICTIONS_LABEL, helpText: NO_PREDICTIONS_TOOLTIP },
        ...labels.map((label) => ({ value: label, label })),
    ];
    const selected = options.find((o) => o.value === value);

    return (
        <div className="cw-fallback-label-section">
            <div className="cw-section-label">
                Fallback label
                {required && <span className="cw-required-asterisk">*</span>}
                <HelpIcon
                    content={
                        <>
                            When confidence is low, the platform predicts the selected{' '}
                            <strong>Fallback</strong> label.
                        </>
                    }
                />
            </div>
            <div className="cw-fallback-label-section__select">
                <DropdownMenu
                    selectType="single-select"
                    showLabel={false}
                    size="medium"
                    placeholder="Select fallback label"
                    text={selected?.label}
                    panelLayout="overlay"
                    panelClassName="cw-label-options-panel"
                    open={open}
                    onOpenChange={setOpen}
                    searchable
                    searchMode="trigger"
                    searchEmptyText="No matching labels"
                    items={options.map((o) => ({
                        label: <TruncatedText text={o.label} maxWidth="100%" />,
                        searchText: o.label,
                        state: o.value === value ? 'selected' : 'default',
                        suffix: o.helpText ? (
                            <span className="spark-dropdown-item-suffix">
                                <OptionHelpIcon text={o.helpText} />
                            </span>
                        ) : undefined,
                        onClick: () => {
                            // Optional mode: re-clicking the selected option
                            // clears the select back to blank.
                            onChange(!required && o.value === value ? null : o.value);
                            setOpen(false);
                        },
                    }))}
                />
            </div>
            {error && <div className="cw-field-error">{error}</div>}
        </div>
    );
};

export default FallbackLabelSelect;
